import React, { useEffect, useState } from 'react';
import md5 from 'crypto-js/md5';
import {
  findRegisteredUser,
  hashPassword,
  saveAuthedUser,
  upsertRegisteredUser,
  verifyPassword
} from '../services/authStorage';
import { isTestAccountPhone, matchTestAccount } from '../services/testAccounts';
import { isAdult, isValidIdCard } from '../utils/idCard';
import './AuthPage.css';

// 手机号正则：1 开头的 11 位数字
const PHONE_REGEX = /^1\d{10}$/;
// 注册密码最小长度
const PASSWORD_MIN_LENGTH = 6;

const AuthPage = ({ onAuthSuccess }) => {
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [idCard, setIdCard] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [lastSentCode, setLastSentCode] = useState('');

  // 短信网关配置（与现有 nginx 反向代理保持一致）
  const smsConfig = {
    url: process.env.REACT_APP_SMS_URL || '/api/sendSms',
    token: process.env.REACT_APP_SMS_TOKEN || 'TKN_9C4E7A2F6D18B5A0',
    salt: process.env.REACT_APP_SMS_SALT || '7E4C9A1D0F6B28E53B94C2A8F17D65AC'
  };

  // 按 key 字典序拼接 JSON，用于签名
  const buildSortedJson = (data) => {
    const sortedKeys = Object.keys(data).sort();
    const sorted = {};
    sortedKeys.forEach((key) => {
      sorted[key] = data[key];
    });
    return JSON.stringify(sorted);
  };

  const buildSign = (data) => {
    const json = buildSortedJson(data);
    return md5(json + smsConfig.salt).toString();
  };

  const generateSmsCode = () => {
    return String(Math.floor(100000 + Math.random() * 900000));
  };

  const handleSendCode = async () => {
    if (!PHONE_REGEX.test(phone)) {
      setSendError('请填写正确的 11 位手机号');
      return;
    }
    // 测试账号已经在白名单里视为已注册，不再发短信
    if (isTestAccountPhone(phone)) {
      setSendError('该手机号已注册，请直接登录');
      return;
    }
    setSendError('');
    setSendLoading(true);
    try {
      const code = generateSmsCode();
      const timestamp = Date.now();
      const signPayload = {
        code,
        phone,
        timestamp,
        token: smsConfig.token
      };
      const sign = buildSign(signPayload);
      const url = `${smsConfig.url}?token=${encodeURIComponent(
        smsConfig.token
      )}&timestamp=${timestamp}&sign=${sign}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code, phone })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.code !== 0) {
        throw new Error(result.msg || '短信发送失败');
      }
      setLastSentCode(code);
      setCountdown(60);
    } catch (error) {
      setSendError(error.message || '短信发送失败');
    } finally {
      setSendLoading(false);
    }
  };

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // 切换登录/注册时清空一次性表单字段，避免误带
  useEffect(() => {
    setSendError('');
    setPassword('');
    setSmsCode('');
    setIdCard('');
  }, [mode]);

  // 注册流程的全部校验，顺序很重要：先廉价校验再贵的
  const validateRegister = () => {
    if (!PHONE_REGEX.test(phone)) {
      return '请填写正确的 11 位手机号';
    }
    if (isTestAccountPhone(phone)) {
      return '该手机号已注册，请直接登录';
    }
    if (!smsCode) {
      return '请输入手机验证码';
    }
    if (lastSentCode && smsCode !== lastSentCode) {
      return '验证码不正确，请重试';
    }
    if (!isValidIdCard(idCard)) {
      return '请填写正确的 18 位身份证号';
    }
    if (!isAdult(idCard)) {
      return '抱歉，未成年人不能注册';
    }
    // TODO(realname-verify): 调用运营商三要素接口校验"手机号实名人 == 身份证持有人"
    // 接口未确定，本期跳过；上线前需要接入。
    if (!password || password.length < PASSWORD_MIN_LENGTH) {
      return `密码至少 ${PASSWORD_MIN_LENGTH} 位`;
    }
    if (findRegisteredUser(phone)) {
      return '该手机号已注册，请直接登录';
    }
    return '';
  };

  const handleRegisterSubmit = () => {
    const errorMsg = validateRegister();
    if (errorMsg) {
      setSendError(errorMsg);
      return;
    }

    const registeredUser = upsertRegisteredUser({
      phone,
      idCard,
      passwordHash: hashPassword(password),
      registeredAt: new Date().toISOString()
    });
    if (!registeredUser) {
      setSendError('注册信息保存失败，请重试');
      return;
    }

    const authed = saveAuthedUser(registeredUser);
    if (onAuthSuccess) {
      onAuthSuccess(authed);
    }
  };

  const handleLoginSubmit = () => {
    if (!PHONE_REGEX.test(phone)) {
      setSendError('请填写正确的 11 位手机号');
      return;
    }
    if (!password) {
      setSendError('请输入密码');
      return;
    }

    // 1) 先匹配测试账号白名单
    const testUser = matchTestAccount(phone, password);
    if (testUser) {
      const authed = saveAuthedUser(testUser);
      if (onAuthSuccess) {
        onAuthSuccess(authed);
      }
      return;
    }

    // 2) 再匹配本地已注册用户（密码哈希）
    const registered = findRegisteredUser(phone);
    if (registered && verifyPassword(password, registered.passwordHash)) {
      const authed = saveAuthedUser(registered);
      if (onAuthSuccess) {
        onAuthSuccess(authed);
      }
      return;
    }

    setSendError('账号或密码不正确');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSendError('');
    if (mode === 'register') {
      handleRegisterSubmit();
    } else {
      handleLoginSubmit();
    }
  };

  const isRegister = mode === 'register';

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <section className="auth-hero">
          <div className="auth-brand">
            <div className="brand-mark">SealSeek</div>
            <p className="brand-tagline">AI驱动的电商增长引擎</p>
          </div>
          <div className="hero-card">
            <h2>让每次决策都有数据支撑</h2>
            <p>
              连接全域数据，洞察增长机会，打造您的专属AI经营助手。
            </p>
            <div className="hero-stats">
              <div>
                <span className="stat-value">30%</span>
                <span className="stat-label">转化提升</span>
              </div>
              <div>
                <span className="stat-value">5min</span>
                <span className="stat-label">快速上手</span>
              </div>
              <div>
                <span className="stat-value">24/7</span>
                <span className="stat-label">智能陪伴</span>
              </div>
            </div>
          </div>
          <div className="hero-footnote">已服务数百家品牌，覆盖全链路运营场景</div>
        </section>

        <section className="auth-card" aria-live="polite">
          <div className="auth-header">
            <div className="auth-tabs" role="tablist" aria-label="登录注册切换">
              <button
                type="button"
                role="tab"
                aria-selected={!isRegister}
                className={`auth-tab ${!isRegister ? 'active' : ''}`}
                onClick={() => setMode('login')}
              >
                登录
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isRegister}
                className={`auth-tab ${isRegister ? 'active' : ''}`}
                onClick={() => setMode('register')}
              >
                注册
              </button>
            </div>
            <h1>{isRegister ? '创建账户' : '欢迎回来'}</h1>
            <p>{isRegister ? '开始您的专属增长体验' : '继续探索您的经营洞察'}</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="phone">手机号</label>
              <input
                id="phone"
                type="tel"
                inputMode="numeric"
                placeholder="请输入11位手机号"
                value={phone}
                onChange={(event) => setPhone(event.target.value.trim())}
                required
              />
            </div>

            {isRegister && (
              <div className="form-field inline-field">
                <label htmlFor="code">验证码</label>
                <div className="inline-input">
                  <input
                    id="code"
                    type="text"
                    placeholder="请输入手机验证码"
                    value={smsCode}
                    onChange={(event) => setSmsCode(event.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={handleSendCode}
                    disabled={sendLoading || countdown > 0}
                  >
                    {countdown > 0 ? `${countdown}s后重试` : sendLoading ? '发送中...' : '发送验证码'}
                  </button>
                </div>
              </div>
            )}

            {isRegister && (
              <div className="form-field">
                <label htmlFor="idcard">身份证号</label>
                <input
                  id="idcard"
                  type="text"
                  placeholder="请输入18位身份证号"
                  value={idCard}
                  onChange={(event) => setIdCard(event.target.value.trim())}
                  onBlur={() => {
                    // 失焦时即时做未成年校验，让用户在填完就能看到提示
                    if (!idCard) return;
                    if (!isValidIdCard(idCard)) {
                      setSendError('请填写正确的 18 位身份证号');
                      return;
                    }
                    if (!isAdult(idCard)) {
                      setSendError('抱歉，未成年人不能注册');
                      return;
                    }
                    // 身份证合法且成年时，清掉这块的错误提示
                    setSendError((prev) =>
                      prev === '请填写正确的 18 位身份证号' || prev === '抱歉，未成年人不能注册'
                        ? ''
                        : prev
                    );
                  }}
                  required
                />
              </div>
            )}

            <div className="form-field password-field">
              <label htmlFor="password">{isRegister ? '设置密码' : '密码'}</label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={isRegister ? `请设置至少 ${PASSWORD_MIN_LENGTH} 位密码` : '请输入您的密码'}
                value={password}
                onChange={(event) => setPassword(event.target.value.trim())}
                required
              />
              <button
                type="button"
                className="icon-button"
                aria-label={showPassword ? '隐藏密码' : '显示密码'}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M2.1 12.1C4.2 7.5 7.8 5 12 5c4.2 0 7.8 2.5 9.9 7.1-.5 1.1-1.2 2.1-2 3L20 15l-1.4 1.4-.9-.9c-1.7 1.5-3.6 2.3-5.7 2.3-4.2 0-7.8-2.5-9.9-7.1z"
                    fill="currentColor"
                    opacity="0.4"
                  />
                  <path
                    d="M12 8.4c-2 0-3.6 1.6-3.6 3.6S10 15.6 12 15.6s3.6-1.6 3.6-3.6S14 8.4 12 8.4zm0 1.8a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>

            {!isRegister && (
              <div className="form-row">
                <label className="checkbox">
                  <input type="checkbox" defaultChecked />
                  记住我
                </label>
                <button type="button" className="link-button">忘记密码？</button>
              </div>
            )}

            {sendError && <div className="field-error">{sendError}</div>}

            <button type="submit" className="primary-button">
              {isRegister ? '立即注册' : '登录'}
            </button>

            <div className="auth-footer">
              {isRegister ? (
                <span>
                  已有账号？
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setMode('login')}
                  >
                    去登录
                  </button>
                </span>
              ) : (
                <span>
                  还没有账号？
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setMode('register')}
                  >
                    立即注册
                  </button>
                </span>
              )}
            </div>
          </form>
        </section>
      </div>

      <footer className="legal-footer">
        <p>玺承控股集团有限公司 All Rights Reserved</p>
        <p>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
            苏ICP备2020056111号-5
          </a>
        </p>
        <p>
          <a
            href="https://beian.mps.gov.cn/#/query/webSearch?code=32010202011403"
            target="_blank"
            rel="noopener noreferrer"
          >
            苏公网安备 32010202011403号
          </a>
        </p>
      </footer>
    </div>
  );
};

export default AuthPage;
